/**
 * Fleet reports: what the record actually says, over a window you choose.
 *
 * Three things this page is careful about, because each is an easy way for a
 * chart to lie:
 *
 * * **Retention is stated, not implied.** Health samples are kept 90 days and
 *   node heartbeats 30. Ask for a year and most of it is a period nobody was
 *   recording — so the coverage line under each chart says how far back the
 *   record goes, and an empty stretch reads as "not recorded" rather than as a
 *   fleet sitting at zero.
 *
 * * **A bucket with no samples is a gap, not 0%.** Availability is null for
 *   such a bucket and nothing is drawn: a bar at zero and a bar that was never
 *   measured are the same picture and opposite facts.
 *
 * * **Every headline says what it is over.** A lone availability figure is
 *   meaningless without its window, so the KPI row repeats the range rather
 *   than leaving the reader to remember which filter is set.
 *
 * Hospital edge nodes are reported by `EdgeHealthSection`, from the per-day
 * rows the cloud ingest keeps, in Baghdad days. It replaced the two raw
 * heartbeat charts that used to sit at the bottom of this page (unreplicated
 * studies and free disk per node): those plotted every stored heartbeat, only
 * 30 days of them, unevenly spaced and capped — the daily record answers the
 * same questions for any window and adds availability and a disk forecast.
 * The "Studies at risk" headline is a statement about right now, so it is
 * read from the live registry (`/api/edge-nodes`, each node's latest
 * `unreplicated_count`) scoped like the rest of the page — not from a windowed
 * series, which would show a past window's last heartbeat as if it were now.
 */

import { useMemo, useState } from 'react';

import { Callout } from '../components/Callout';
import { PageHeader } from '../components/PageHeader';
import { ReportTabs } from '../components/ReportTabs';
import { EdgeHealthSection } from '../components/reports/EdgeHealthSection';
import { StatGrid, StatTile } from '../components/StatTile';
import { ErrorBlock } from '../components/States';
import { Card } from '../components/ui/Card';
import { Select, SelectItem, TextInput } from '../components/ui/Form';
import {
  ChartFrame,
  LineChart,
  Meter,
  RankedBars,
  StateBars,
} from '../components/viz/Chart';
import type { Series } from '../components/viz/Chart';
import { addDays, baghdadToday, daysInclusive, isDay } from '../lib/baghdad';
import { formatBytes, formatDateTime, formatNumber, UNKNOWN } from '../lib/format';
import { useResource } from '../lib/useResource';
import type {
  CenterListResponse,
  CenterSeriesResponse,
  EdgeNodeListResponse,
} from '../types/api';

/** Windows an operator actually asks for, each with the bucket that suits it. */
const PRESETS = [
  // The server takes whole Baghdad days, so this preset serves yesterday
  // 00:00 Baghdad until now (24-48 hours); the label says exactly that.
  { key: '24h', label: 'Yesterday and today', days: 1, bucket: 'hour' },
  { key: '7d', label: 'Last 7 days', days: 7, bucket: 'day' },
  { key: '30d', label: 'Last 30 days', days: 30, bucket: 'day' },
  { key: '90d', label: 'Last 90 days', days: 90, bucket: 'week' },
  { key: '365d', label: 'Last 12 months', days: 365, bucket: 'month' },
  { key: 'custom', label: 'Custom range…', days: 0, bucket: 'day' },
] as const;

const BUCKETS = [
  { key: 'hour', label: 'Hourly' },
  { key: 'day', label: 'Daily' },
  { key: 'week', label: 'Weekly' },
  { key: 'month', label: 'Monthly' },
] as const;

type PresetKey = (typeof PRESETS)[number]['key'];

/** A percentage, or the UNKNOWN marker — never 0% standing in for "not known". */
function percent(value: number | null | undefined): string {
  return value === null || value === undefined ? UNKNOWN : `${(value * 100).toFixed(1)}%`;
}

// Stable empties for a disabled resource, so the memos below keep their deps.
const NO_CENTER_POINTS: CenterSeriesResponse['points'] = [];

export function ReportsPage() {
  const [presetKey, setPresetKey] = useState<PresetKey>('7d');
  // Custom ranges are Baghdad calendar days, like the upload reports: a date
  // picker's "the 28th" means the 28th in Baghdad, whatever zone this browser
  // is in. The old UTC-midnight bounds put three hours of the previous
  // Baghdad evening into every day and cut three off its end.
  const [customFrom, setCustomFrom] = useState(() => addDays(baghdadToday(), -29));
  const [customTo, setCustomTo] = useState(() => baghdadToday());
  const [bucketOverride, setBucketOverride] = useState<string>('');
  /** '' = whole fleet · 'center:<slug>' · 'node:<uuid>' */
  const [scope, setScope] = useState('');

  const preset = PRESETS.find((p) => p.key === presetKey) ?? PRESETS[2];
  const isCustom = presetKey === 'custom';
  const bucket = bucketOverride || preset.bucket;

  const centerScope = scope.startsWith('center:') ? scope.slice(7) : '';
  const nodeScope = scope.startsWith('node:') ? scope.slice(5) : '';

  // The same window as Baghdad days, for the day-grained edge section. A
  // preset of N days is today and the N-1 before it; the one-day hourly preset spans
  // two Baghdad days, so it asks for yesterday and today.
  const today = baghdadToday();
  const edgeDays = useMemo(() => {
    if (isCustom) return { from: customFrom, to: customTo };
    return { from: addDays(today, -Math.max(1, preset.days - 1)), to: today };
  }, [isCustom, customFrom, customTo, preset, today]);
  const edgeBucket = bucket === 'week' || bucket === 'month' ? bucket : 'day';

  // The server refuses (400) a window that starts in the future or spans more
  // than this many days (backend MAX_WINDOW_DAYS); its end is clamped to today.
  const EDGE_MAX_DAYS = 400;

  // The server window is the same inclusive Baghdad days as `edgeDays`, sent
  // as YYYY-MM-DD: `from` and `to` are both counted, whole days. A custom
  // window is checked against the server's own day_window rules first, so a
  // bad one gets a validation message rather than three 400s.
  const { from, to, rangeLabel, invalid, invalidReason } = useMemo(() => {
    if (isCustom) {
      const invalidReason: 'malformed' | 'backwards' | 'future' | 'too-long' | null =
        !isDay(customFrom) || !isDay(customTo)
          ? 'malformed'
          : customFrom > customTo
            ? 'backwards'
            : customFrom > today
              ? 'future'
              : daysInclusive(customFrom, customTo > today ? today : customTo) > EDGE_MAX_DAYS
                ? 'too-long'
                : null;
      const bad = invalidReason !== null;
      return {
        invalidReason,
        from: bad ? '' : edgeDays.from,
        to: bad ? '' : edgeDays.to,
        rangeLabel:
          customFrom === customTo
            ? `${customFrom} (Baghdad)`
            : `${customFrom} to ${customTo} (Baghdad)`,
        invalid: bad,
      };
    }
    return {
      from: edgeDays.from,
      to: edgeDays.to,
      rangeLabel: preset.label.toLowerCase(),
      invalid: false,
      invalidReason: null,
    };
  }, [isCustom, customFrom, customTo, preset, edgeDays, today]);

  // The edge report refuses the same windows; asking anyway only earns a 400
  // and an error card. Its end is clamped to today, as the server would.
  const edgeTo = edgeDays.to > today ? today : edgeDays.to;
  const edgeReady =
    !invalid && edgeDays.from <= today && daysInclusive(edgeDays.from, edgeTo) <= EDGE_MAX_DAYS;

  // The pickers need something to pick from.
  const centerList = useResource<CenterListResponse>('/api/centers');
  const nodeList = useResource<EdgeNodeListResponse>('/api/edge-nodes');

  const query = (extra: string) =>
    `from=${encodeURIComponent(from)}` + (to ? `&to=${encodeURIComponent(to)}` : '') + extra;

  // Always `per_center`, even when scoped to one: the slug has to come back on
  // each point for the ranking and the per-center lines to have anything to key
  // on. The fleet totals are a second, cheaper query rather than a client-side
  // sum, so the availability headline is the database's arithmetic and not this
  // component's.
  const centers = useResource<CenterSeriesResponse>(
    `/api/reports/centers?${query(
      `&bucket=${bucket}&per_center=true` +
        (centerScope ? `&slug=${encodeURIComponent(centerScope)}` : ''),
    )}`,
    { enabled: !invalid && !nodeScope },
  );
  const fleetTotals = useResource<CenterSeriesResponse>(
    `/api/reports/centers?${query(
      `&bucket=${bucket}` + (centerScope ? `&slug=${encodeURIComponent(centerScope)}` : ''),
    )}`,
    { enabled: !invalid && !nodeScope },
  );

  /** Centers are worked with by name; the slug is the machine's handle for
   *  them and is kept beside it rather than instead of it — it is the
   *  subdomain label, so an operator still needs to see it. */
  const centerNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const center of centerList.data?.centers ?? []) {
      if (center.name) names.set(center.slug, center.name);
    }
    return names;
  }, [centerList.data]);
  const nameFor = (slug: string) => centerNames.get(slug) || slug;

  // useResource keeps `data` when `enabled` turns false, so a disabled
  // resource's last window must be ignored here: an invalid range or a node
  // scope would otherwise show the previous window's (or the whole fleet's)
  // numbers under the new label.
  const centersOn = !invalid && !nodeScope;
  const centerPoints = centersOn ? centers.data?.points ?? NO_CENTER_POINTS : NO_CENTER_POINTS;
  const fleetPoints = centersOn ? fleetTotals.data?.points ?? NO_CENTER_POINTS : NO_CENTER_POINTS;

  const slugs = useMemo(
    () => [...new Set(centerPoints.map((p) => p.slug).filter(Boolean))].sort() as string[],
    [centerPoints],
  );

  // ── the headline numbers ───────────────────────────────────────────────────

  const kpis = useMemo(() => {
    const samples = fleetPoints.reduce((total, p) => total + p.samples, 0);
    const up = fleetPoints.reduce((total, p) => total + p.up, 0);
    const availability = samples > 0 ? up / samples : null;

    const latencies = fleetPoints
      .map((p) => p.p95_latency_ms)
      .filter((v): v is number => v !== null);
    const worstLatency = latencies.length > 0 ? Math.max(...latencies) : null;

    // The fleet point's `studies` is the SUM of each center's figure for the
    // bucket (it used to be the single largest center's, which is what the
    // tile was then labelled as the total of). `centers_reporting` says how
    // many centers that sum is over — without it, a bucket where half the
    // fleet failed its probe would read as the fleet halving its archive.
    const last = fleetPoints[fleetPoints.length - 1];
    const studies = last ? last.studies : null;
    const centersReporting = last?.centers_reporting ?? null;

    return { availability, worstLatency, studies, centersReporting, samples };
  }, [fleetPoints]);

  // Studies at risk: data that exists on a node and nowhere else, right now.
  // Read from the live registry, scoped to the node or center in view; an
  // unreadable registry or no node reporting the count is UNKNOWN, not zero.
  const atRisk = useMemo(() => {
    const liveNodes = nodeList.data && !nodeList.data.error ? nodeList.data.nodes : null;
    const scoped = liveNodes?.filter((n) =>
      nodeScope ? n.node_uuid === nodeScope : centerScope ? n.center_slug === centerScope : true,
    );
    const known =
      scoped?.map((n) => n.unreplicated_count).filter((v): v is number => v !== null) ?? [];
    return known.length > 0 ? known.reduce((a, b) => a + b, 0) : null;
  }, [nodeList.data, nodeScope, centerScope]);

  /** Availability per center over the whole window, worst first. */
  const availabilityByCenter = useMemo(() => {
    const totals = new Map<string, { up: number; samples: number }>();
    for (const point of centerPoints) {
      if (!point.slug) continue;
      const entry = totals.get(point.slug) ?? { up: 0, samples: 0 };
      entry.up += point.up;
      entry.samples += point.samples;
      totals.set(point.slug, entry);
    }
    return [...totals.entries()]
      .map(([slug, t]) => ({
        key: slug,
        label: (
          <>
            {nameFor(slug)}
            {centerNames.has(slug) ? <span className="muted"> · {slug}</span> : null}
          </>
        ),
        value: t.samples > 0 ? t.up / t.samples : null,
      }))
      // Worst first, and centers with no samples last: "unknown" is not "bad",
      // and sorting it to the top would put a gap in the record where an
      // outage belongs.
      .sort((a, b) => (a.value ?? 2) - (b.value ?? 2));
  }, [centerPoints, centerNames]);

  /** Peak storage per center in this window, largest first. */
  const diskByCenter = useMemo(() => {
    const peak = new Map<string, number>();
    for (const point of centerPoints) {
      if (!point.slug || point.disk_size_bytes === null) continue;
      peak.set(point.slug, Math.max(peak.get(point.slug) ?? 0, point.disk_size_bytes));
    }
    return [...peak.entries()].sort((a, b) => b[1] - a[1]);
  }, [centerPoints]);

  const seriesFor = (
    keys: string[],
    pick: (key: string) => { at: string; value: number | null }[],
    naming: (key: string) => string = (key) => key,
  ): Series[] =>
    keys.slice(0, 8).map((key, index) => {
      const name = naming(key);
      return {
        key,
        // A uuid is truncated because it is an identifier nobody reads in full;
        // a center's name is not, because the whole point of showing it is that
        // it is the thing a person recognises.
        label: name === key && key.length > 12 ? `${key.slice(0, 8)}…` : name,
        slot: index + 1,
        points: pick(key),
      };
    });

  const latencySeries = useMemo<Series[]>(
    () =>
      fleetPoints.length === 0
        ? []
        : [
            {
              key: 'avg',
              label: 'Average',
              slot: 1,
              points: fleetPoints.map((p) => ({ at: p.bucket, value: p.avg_latency_ms })),
            },
            {
              key: 'p95',
              label: '95th percentile',
              slot: 2,
              points: fleetPoints.map((p) => ({ at: p.bucket, value: p.p95_latency_ms })),
            },
          ],
    [fleetPoints],
  );

  const studiesSeries = useMemo(
    () =>
      seriesFor(
        slugs,
        (slug) =>
          centerPoints
            .filter((p) => p.slug === slug)
            .map((p) => ({ at: p.bucket, value: p.studies })),
        nameFor,
      ),
    [slugs, centerPoints, centerNames],
  );

  const fleetBuckets = useMemo(
    () =>
      fleetPoints.map((p) => ({
        at: p.bucket,
        up: p.up,
        degraded: p.degraded,
        down: p.down,
        unknown: p.unknown,
        samples: p.samples,
      })),
    [fleetPoints],
  );

  const coverageNote = (
    coverage: { earliest: string | null; rows: number } | undefined,
    retentionDays: number,
  ) => {
    if (!coverage || coverage.rows === 0) return 'Nothing recorded yet.';
    return (
      <>
        {formatNumber(coverage.rows)} samples on record, the earliest from{' '}
        {coverage.earliest ? formatDateTime(coverage.earliest) : UNKNOWN}. Kept for{' '}
        {retentionDays} days — anything before that was never retained, not zero.
      </>
    );
  };

  return (
    <>
      <PageHeader title="Reports" subtitle="Health, capacity and replication over time" />

      <div className="bento">
        <ReportTabs className="bento__full" />

        {/* ── filters ────────────────────────────────────────────────────── */}
        <div className="bento__full toolbar">
          <Select
            id="report-range"
            labelText="Window"
            value={presetKey}
            onChange={(event) => setPresetKey(event.target.value as PresetKey)}
          >
            {PRESETS.map((option) => (
              <SelectItem key={option.key} value={option.key} text={option.label} />
            ))}
          </Select>

          {isCustom ? (
            <>
              <TextInput
                id="report-from"
                labelText="From"
                type="date"
                value={customFrom}
                onChange={(event) => setCustomFrom(event.target.value)}
              />
              <TextInput
                id="report-to"
                labelText="To"
                type="date"
                value={customTo}
                onChange={(event) => setCustomTo(event.target.value)}
              />
            </>
          ) : null}

          <Select
            id="report-bucket"
            labelText="Bucket"
            value={bucket}
            onChange={(event) => setBucketOverride(event.target.value)}
          >
            {BUCKETS.map((option) => (
              <SelectItem key={option.key} value={option.key} text={option.label} />
            ))}
          </Select>

          <Select
            id="report-scope"
            labelText="Scope"
            value={scope}
            onChange={(event) => setScope(event.target.value)}
          >
            <SelectItem value="" text="Whole fleet" />
            {(centerList.data?.centers ?? []).map((center) => (
              <SelectItem
                key={center.slug}
                value={`center:${center.slug}`}
                text={`Center — ${center.name || center.slug}`}
              />
            ))}
            {(nodeList.data?.nodes ?? []).map((node) => (
              <SelectItem
                key={node.node_uuid}
                value={`node:${node.node_uuid}`}
                text={`Node — ${node.label || node.node_uuid.slice(0, 8)}`}
              />
            ))}
          </Select>
        </div>

        {invalidReason === 'backwards' ? (
          <Callout className="bento__full" tone="warning" title="That range is backwards">
            The start date must come before the end date.
          </Callout>
        ) : invalidReason === 'future' ? (
          <Callout className="bento__full" tone="warning" title="That range starts in the future">
            The start date must be today ({today}, Baghdad) or earlier.
          </Callout>
        ) : invalidReason === 'too-long' ? (
          <Callout className="bento__full" tone="warning" title="That range is longer than 400 days">
            Reports cover at most {EDGE_MAX_DAYS} days, ending today at the latest. Choose a
            later start date.
          </Callout>
        ) : invalidReason === 'malformed' ? (
          <Callout className="bento__full" tone="warning" title="That range is not a date range">
            Enter both dates as YYYY-MM-DD.
          </Callout>
        ) : null}

        {/* ── headline ───────────────────────────────────────────────────── */}
        <div className="bento__full">
          <StatGrid>
            <StatTile
              label="Availability"
              value={percent(kpis.availability)}
              hint={`of ${formatNumber(kpis.samples)} probes, ${rangeLabel}`}
              tone={
                kpis.availability === null
                  ? undefined
                  : kpis.availability >= 0.99
                    ? 'good'
                    : kpis.availability >= 0.95
                      ? 'warning'
                      : 'critical'
              }
            />
            <StatTile
              label="Studies at risk"
              value={atRisk === null ? UNKNOWN : formatNumber(atRisk)}
              hint="on a node and nowhere else, now"
              tone={atRisk ? 'critical' : undefined}
            />
            <StatTile
              label="Studies"
              value={kpis.studies === null ? UNKNOWN : formatNumber(kpis.studies)}
              hint={
                centerScope
                  ? 'this center, latest bucket'
                  : kpis.centersReporting === null
                    ? 'fleet total, latest bucket'
                    : `sum of ${formatNumber(kpis.centersReporting)} ${
                        kpis.centersReporting === 1 ? 'center' : 'centers'
                      }, latest bucket`
              }
            />
            <StatTile
              label="Worst p95 latency"
              value={kpis.worstLatency === null ? UNKNOWN : `${Math.round(kpis.worstLatency)} ms`}
              hint={`across ${rangeLabel}`}
            />
          </StatGrid>
        </div>

        {centersOn && centers.error ? (
          <Card className="bento__full">
            <div className="card__body">
              <ErrorBlock
                what="the center history"
                error={centers.error}
                onRetry={centers.reload}
              />
            </div>
          </Card>
        ) : null}

        {centersOn && centers.data?.truncated ? (
          <Callout className="bento__full" tone="warning" title="Center series truncated">
            This window holds more center buckets than one response carries, so
            only the most recent are shown
            {centerPoints[0] ? <> — from {formatDateTime(centerPoints[0].bucket)}</> : null}.
            The oldest part of the window is missing, not empty. Choose a coarser
            bucket or a shorter window to see all of it.
          </Callout>
        ) : null}

        {/* ── charts ─────────────────────────────────────────────────────── */}
        {nodeScope ? null : (
          <Card className="bento__two-thirds">
            <div className="card__body">
              <ChartFrame
                title="Fleet state over time"
                subtitle="Share of probes in each state. A bucket with no samples is drawn as nothing — a gap in the record, not an outage."
                coverage={coverageNote(centersOn ? fleetTotals.data?.coverage : undefined, 90)}
                unavailable={centersOn ? fleetTotals.data?.unavailable_reason : undefined}
                table={
                  <div
                    className="table-scroll"
                    tabIndex={0}
                    role="region"
                    aria-label="Fleet state"
                  >
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th scope="col">Bucket</th>
                          <th scope="col" className="numeric">Samples</th>
                          <th scope="col" className="numeric">Up</th>
                          <th scope="col" className="numeric">Down</th>
                          <th scope="col" className="numeric">Availability</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fleetBuckets.map((b) => (
                          <tr key={b.at}>
                            <td>{formatDateTime(b.at)}</td>
                            <td className="numeric">{formatNumber(b.samples)}</td>
                            <td className="numeric">{formatNumber(b.up)}</td>
                            <td className="numeric">{formatNumber(b.down)}</td>
                            <td className="numeric">
                              {percent(b.samples > 0 ? b.up / b.samples : null)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                }
              >
                <StateBars buckets={fleetBuckets} label="Fleet state over time" />
              </ChartFrame>
            </div>
          </Card>
        )}

        {nodeScope ? null : (
          <Card className="bento__third bento__tall">
            <div className="card__body">
              <ChartFrame
                title="Availability by center"
                subtitle="Worst first, over the whole window. One hue: this is a ranking, not a set of identities."
                unavailable={
                  availabilityByCenter.length === 0
                    ? 'No center samples in this window.'
                    : undefined
                }
              >
                <RankedBars
                  rows={availabilityByCenter}
                  label="Availability by center"
                  format={(value) => `${(value * 100).toFixed(1)}%`}
                  tone={(value) =>
                    value >= 0.99
                      ? 'var(--status-good)'
                      : value >= 0.95
                        ? 'var(--status-warning)'
                        : 'var(--status-critical)'
                  }
                />
              </ChartFrame>
            </div>
          </Card>
        )}

        {nodeScope ? null : (
          <Card className="bento__two-thirds">
            <div className="card__body">
              <ChartFrame
                title="Probe latency"
                subtitle="Failed probes carry no latency and are excluded — never counted as zero."
                series={latencySeries}
                unavailable={
                  latencySeries.length === 0 ? 'No latency recorded in this window.' : undefined
                }
              >
                <LineChart
                  series={latencySeries}
                  label="Probe latency over time"
                  format={(value) => `${Math.round(value)} ms`}
                />
              </ChartFrame>
            </div>
          </Card>
        )}

        {nodeScope ? null : (
          <Card className="bento__half">
            <div className="card__body">
              <ChartFrame
                title="Studies per center"
                series={studiesSeries}
                unavailable={studiesSeries.length === 0 ? 'No studies recorded.' : undefined}
              >
                <LineChart
                  series={studiesSeries}
                  label="Studies per center over time"
                  format={(value) => formatNumber(Math.round(value))}
                />
              </ChartFrame>
            </div>
          </Card>
        )}

        {nodeScope || diskByCenter.length === 0 ? null : (
          <Card className="bento__half">
            <div className="card__body">
              <ChartFrame
                title="Storage by center"
                subtitle="Peak in this window, against the largest center. Capacity is not reported, so this is a comparison and NOT a fill level."
              >
                <div className="stack">
                  {diskByCenter.map(([slug, bytes]) => (
                    <div key={slug}>
                      <div className="row">
                        <span className="viz__ranked-label">
                          {nameFor(slug)}
                          {centerNames.has(slug) ? (
                            <span className="muted"> · {slug}</span>
                          ) : null}
                        </span>
                        <span className="push mono">{formatBytes(bytes)}</span>
                      </div>
                      <Meter
                        value={bytes}
                        max={diskByCenter[0]![1]}
                        label={`${slug} storage relative to the largest center`}
                      />
                    </div>
                  ))}
                </div>
              </ChartFrame>
            </div>
          </Card>
        )}

        {/* ── hospital edge nodes ────────────────────────────────────────── */}
        <EdgeHealthSection
          fromDay={edgeDays.from}
          toDay={edgeTo}
          bucket={edgeBucket}
          slug={centerScope || null}
          node={nodeScope || null}
          liveNodes={nodeList.data?.nodes}
          ready={edgeReady}
          note={
            !invalid && !edgeReady
              ? `Edge health covers at most ${EDGE_MAX_DAYS} days, ending today.`
              : bucket === 'hour'
                ? 'Edge health is recorded per day, so an hourly window is shown by day.'
                : undefined
          }
        />
      </div>
    </>
  );
}
